import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Trash2, 
  CheckCircle2, 
  ArrowLeft, 
  AlertTriangle,
  Loader2,
  ShieldAlert,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function DataDeletionPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Info, 2: Confirm, 3: Success
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [confirmations, setConfirmations] = useState({
    understand: false,
    permanent: false,
    chatwoot: false
  });
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deletionRequestId, setDeletionRequestId] = useState('');

  const allConfirmed = confirmations.understand && confirmations.permanent && confirmations.chatwoot;

  const handleRequestDeletion = async () => {
    if (!allConfirmed) {
      setError('Please confirm all checkboxes to proceed');
      return;
    }

    if (confirmEmail.toLowerCase() !== user?.email?.toLowerCase()) {
      setError('Email address does not match your account');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/user/request-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit deletion request');
      }

      setDeletionRequestId(data.request_id);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoutAfterDeletion = () => {
    logout();
    navigate('/');
  };

  // Step 3: Success State
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="deletion-success-page">
        <Header />
        
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <Card className="max-w-md w-full p-8 text-center bg-white border border-gray-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Deletion Request Submitted
            </h1>
            
            <p className="text-gray-600 mb-4">
              Your data deletion request has been received and is being processed.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Request ID:</span> 
                <code className="ml-2 bg-gray-200 px-2 py-0.5 rounded text-xs">{deletionRequestId}</code>
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <span className="font-medium">Processing Time:</span> Up to 30 days
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <span className="font-medium">Confirmation:</span> You will receive an email when complete
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Your GiriChat account will be deactivated</li>
                <li>Your Chatwoot account will be scheduled for deletion</li>
                <li>All personal data will be permanently removed</li>
                <li>You will receive a confirmation email</li>
              </ul>
            </div>

            <Button 
              onClick={handleLogoutAfterDeletion}
              className="w-full bg-[#00D95F] hover:bg-[#00C054]"
            >
              Sign Out
            </Button>

            <p className="text-xs text-gray-500 mt-4">
              For questions, contact{' '}
              <a href="mailto:privacy@6miles.io" className="text-[#00D95F] hover:underline">
                privacy@6miles.io
              </a>
            </p>
          </Card>
        </div>

        <Footer />
      </div>
    );
  }

  // Step 2: Confirmation
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="deletion-confirm-page">
        <Header />
        
        <div className="flex-1 py-12 px-4">
          <div className="max-w-lg mx-auto">
            <Button
              variant="ghost"
              className="mb-4 p-0 h-auto text-gray-600 hover:text-gray-900"
              onClick={() => setStep(1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <Card className="p-8 bg-white border border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldAlert className="w-8 h-8 text-red-600" />
                </div>
                
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Confirm Data Deletion
                </h1>
                
                <p className="text-gray-600">
                  Please confirm you understand the consequences of this action.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="understand"
                    checked={confirmations.understand}
                    onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, understand: checked }))}
                    className="mt-1"
                  />
                  <label htmlFor="understand" className="text-sm text-gray-700">
                    I understand that this action will permanently delete all my data from GiriChat, including my profile, settings, and usage history.
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="permanent"
                    checked={confirmations.permanent}
                    onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, permanent: checked }))}
                    className="mt-1"
                  />
                  <label htmlFor="permanent" className="text-sm text-gray-700">
                    I understand that this action is <strong>irreversible</strong> and my data cannot be recovered after deletion.
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="chatwoot"
                    checked={confirmations.chatwoot}
                    onCheckedChange={(checked) => setConfirmations(prev => ({ ...prev, chatwoot: checked }))}
                    className="mt-1"
                  />
                  <label htmlFor="chatwoot" className="text-sm text-gray-700">
                    I understand that my connected WhatsApp Business API account and Chatwoot workspace will also be deleted.
                  </label>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="confirmEmail" className="text-gray-900">
                  Type your email to confirm: <strong>{user?.email}</strong>
                </Label>
                <Input
                  id="confirmEmail"
                  type="email"
                  placeholder="Enter your email address"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  data-testid="confirm-email-input"
                />
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="reason" className="text-gray-900">
                  Reason for leaving (optional)
                </Label>
                <textarea
                  id="reason"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00D95F] focus:border-transparent"
                  rows={3}
                  placeholder="Help us improve by sharing why you're leaving..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <Button 
                  onClick={handleRequestDeletion}
                  disabled={isLoading || !allConfirmed || confirmEmail.toLowerCase() !== user?.email?.toLowerCase()}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="submit-deletion-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Permanently Delete My Data
                </Button>

                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Step 1: Information
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="deletion-info-page">
      <Header />
      
      <div className="flex-1 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            className="mb-4 p-0 h-auto text-gray-600 hover:text-gray-900"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="p-8 bg-white border border-gray-200">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Request Data Deletion
              </h1>
              
              <p className="text-gray-600">
                We're sorry to see you go. Please review the information below before proceeding.
              </p>
            </div>

            {user && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900 mb-2">Account to be deleted:</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Name:</span> {user.full_name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
                {user.chatwoot_user_id && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Chatwoot Account:</span> Connected (ID: {user.chatwoot_account_id})
                  </p>
                )}
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-2">This action will permanently delete:</p>
                  <ul className="list-disc list-inside space-y-1 text-red-700">
                    <li>Your GiriChat account and profile</li>
                    <li>All personal information (name, email, phone)</li>
                    <li>Your subscription and billing history</li>
                    <li>Your connected WhatsApp Business API configuration</li>
                    <li>Your Chatwoot workspace and conversation history</li>
                    <li>All associated data and settings</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-blue-900 mb-2">Data Retention Policy</h3>
              <p className="text-sm text-blue-700">
                In accordance with GDPR and applicable data protection laws, your deletion request will be processed within <strong>30 days</strong>. Some data may be retained for legal compliance purposes (e.g., financial records) as outlined in our{' '}
                <Link to="/privacy" className="underline hover:text-blue-900">Privacy Policy</Link>.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-yellow-900 mb-2">Before you go...</h3>
              <p className="text-sm text-yellow-700 mb-3">
                If you're experiencing issues, we'd love to help! Consider these alternatives:
              </p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• <Link to="/unsubscribe" className="underline hover:text-yellow-900">Unsubscribe from communications</Link> instead of deleting your account</li>
                <li>• <Link to="/contact" className="underline hover:text-yellow-900">Contact our support team</Link> to resolve any issues</li>
                <li>• Take a break - you can always come back later</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={() => setStep(2)}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                data-testid="proceed-deletion-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Proceed with Data Deletion
              </Button>

              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#00D95F] hover:bg-[#00C054]"
              >
                Keep My Account
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Questions about data deletion? Contact our Privacy Team at{' '}
                <a href="mailto:privacy@6miles.io" className="text-[#00D95F] hover:underline">
                  privacy@6miles.io
                </a>
              </p>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
